import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { PlusCircle } from 'lucide-react';
import { Subtype } from '../../types/ServiceAccordion.type';
import {
  CourseLevel,
  CourseStage,
  SuccessResponse,
} from '../../types/CourseStageAccordion.type';
import { apiDelete } from '../../utils/apiClient';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import CourseStageCard from './CourseStageCard';
import CourseStageDialog from '../FormDialog/CourseStageDialog';

interface CourseStageListProps {
  courseType: Subtype;
  level: CourseLevel;
  stages: CourseStage[];
  fetchCourses: () => void;
}

const CourseStageList: React.FC<CourseStageListProps> = ({
  courseType,
  level,
  stages,
  fetchCourses,
}) => {
  const [openCourseStageDialog, setOpenCourseStageDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedCourseStage, setSelectedCourseStage] =
    useState<CourseStage | null>(null);

  const handleCloseCourseStageDialog = () => {
    setOpenCourseStageDialog(false);
    setSelectedCourseStage(null);
  };

  const handleConfirmDeleteCourseStage = async (force: boolean) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/course/stage/delete/${selectedCourseStage?.id}?force=${force}`,
      );

      notifyAPIClient(res.message);
      setOpenDeleteDialog(false);
      setSelectedCourseStage(null);
      fetchCourses();
    } catch (error) {
      console.error('Error deleting stage:', error);
      notifyFailure();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" component="h4" fontWeight="bold">
          {level.levelName} Stages
        </Typography>

        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<PlusCircle size={18} />}
          sx={{
            color: 'white',
          }}
          onClick={() => {
            setSelectedCourseStage(null);
            setOpenCourseStageDialog(true);
          }}
        >
          Add Stage
        </Button>
      </Box>

      {stages.length === 0 ||
      (stages.length === 1 && stages[0].stageName === null) ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ fontStyle: 'italic', py: 1 }}
        >
          No stages available
        </Typography>
      ) : (
        stages.map((stage) => (
          <CourseStageCard
            key={stage.id}
            stage={stage}
            onEdit={(stage) => {
              setSelectedCourseStage(stage);
              setOpenCourseStageDialog(true);
            }}
            onDelete={(stage) => {
              setSelectedCourseStage(stage);
              setOpenDeleteDialog(true);
            }}
          />
        ))
      )}

      <CourseStageDialog
        open={openCourseStageDialog}
        handleClose={handleCloseCourseStageDialog}
        courseStage={selectedCourseStage}
        courseLevel={level}
        courseType={courseType}
        fetchCourses={fetchCourses}
      />

      {/* Delete dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Stage</DialogTitle>
        <DialogContent>
          Do you want to delete this stage? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="contained"
            color="error"
          >
            Close
          </Button>
          <Button
            onClick={() => handleConfirmDeleteCourseStage(false)}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseStageList;
